import { Component, OnInit, ViewChild, signal, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService, SortMeta } from 'primeng/api';
import { RubroService } from '../../services/rubro/rubro.service';
import { DialogService } from '../../shared/services/dialog.service';
import { Rubro } from '../../domain/rubro.domain';
import { environment } from '../../../environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { HttpParams } from '@angular/common/http';

interface Column {
  field: string;
  header: string;
  fieldType?: 'text' | 'boolean' | 'boolean-icon' | 'number' | 'date';
  sortable?: boolean;
  exportable?: boolean;
  filtrable?: boolean;
  modeDisplayFilter?: 'menu' | 'row';
}

@Component({
  selector: 'app-rubros',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    RippleModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './rubros.component.html',
  styleUrls: ['./rubros.component.scss']
})
export class RubrosComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table: Table | undefined;

  // Signals para estado reactive
  rubros = signal<Rubro[]>([]);
  loading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  selectedRows: Rubro[] = [];
  filterValue: string = '';

  // Configuración
  nroFilasPorPagina: number = environment.REGISTROS_PER_PAGE || 10;
  paginaActual = 0;
  paginate = true;
  rowsPerPageOptions: number[] = [];

  // Control de suscripciones y reintentos
  private destroy$ = new Subject<void>();
  private errorCount = 0;
  private readonly MAX_ERROR_COUNT = 3;
  private lastErrorTimestamp = 0;
  private readonly ERROR_RESET_TIMEOUT = 60000; // 1 minuto

  // Columnas de la tabla
  columnsTable: Column[] = [
    { field: 'codigo', header: 'Código', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'nombre', header: 'Nombre', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'descripcion', header: 'Descripción', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'active', header: 'Activo', fieldType: 'boolean-icon', sortable: true, exportable: false, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'accion', header: 'Acciones', sortable: false, filtrable: false }
  ];

  // Para filtros y ordenamiento
  multiSortMeta: SortMeta[] = [];
  filtersMetadata: any = {};

  // Helper para limpiar tabla
  clearTableInProgress = false;
  clearTableCountLazyExecuted = 0;

  constructor(
    private rubroService: RubroService,
    private router: Router,
    private messageService: MessageService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService
  ) {
    if (this.rowsPerPageOptions.length === 0) {
      this.rowsPerPageOptions = environment.REGISTROS_PER_PAGE_OPTIONS || [10, 20, 30, 50];
    }
  }

  ngOnInit(): void {
    this.loading.set(false);
    this.clearTableInProgress = false;
    this.clearTableCountLazyExecuted = 0;
    this.errorCount = 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLazy(event: TableLazyLoadEvent): void {
    // Manejo del bug de bucle infinito al hacer clear
    if (this.clearTableInProgress && this.clearTableCountLazyExecuted === 0) {
      this.clearTableCountLazyExecuted++;
      if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
        this.paginaActual = Math.floor(event.first / event.rows) + 1;
      } else {
        this.paginaActual = 1;
      }
      this.processLazyEvent(event);
      this.cargar();
    } else if (this.clearTableInProgress) {
      return;
    }

    this.processLazyEvent(event);
    this.cargar();
  }

  private processLazyEvent(event: TableLazyLoadEvent): void {
    if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
      // API Platform usa páginas basadas en 1, PrimeNG usa índice basado en 0
      this.paginaActual = Math.floor(event.first / event.rows) + 1;
      this.nroFilasPorPagina = event.rows;
    } else {
      this.paginaActual = 1;
      this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE ?? 10;
    }
    this.multiSortMeta = event.multiSortMeta || [];
    this.filterValue = typeof event.globalFilter === 'string' ? event.globalFilter : '';
    this.filtersMetadata = event.filters || {};
  }

  getFilterType(col: Column): string {
    if (!col.fieldType) return 'text';
    if (col.fieldType === 'date') return 'date';
    if (col.fieldType === 'boolean' || col.fieldType === 'boolean-icon') return 'boolean';
    if (col.fieldType === 'number') return 'numeric';
    return 'text';
  }

  getMatchMode(fieldType?: string): string {
    if (fieldType === 'boolean') return 'equals';
    if (fieldType === 'number') return 'equals';
    return 'contains';  // Para texto
  }

  cargar(): void {
    // Verificar si hemos excedido el límite de errores
    const now = Date.now();
    if (now - this.lastErrorTimestamp > this.ERROR_RESET_TIMEOUT) {
      this.errorCount = 0;
    }

    if (this.errorCount >= this.MAX_ERROR_COUNT) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Se alcanzó el límite de reintentos (${this.MAX_ERROR_COUNT}). Por favor, recarga la página o contacta al administrador.`,
        life: 8000,
        sticky: true
      });
      return;
    }

    this.loading.set(true);

    this.rubroService.findAll(
      this.paginate,
      this.paginaActual,
      this.nroFilasPorPagina,
      this.filterValue || '',
      this.multiSortMeta,
      this.filtersMetadata
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading.set(false);
          this.errorCount = 0;
          this.lastErrorTimestamp = 0;

          const members = resp['hydra:member'] || resp.member || resp;

          const rubrosMapeados = members.map((rubro: any) => ({
            id: rubro.id,
            codigo: rubro.codigo,
            nombre: rubro.nombre,
            descripcion: rubro.descripcion,
            active: rubro.active ?? true
          }));

          this.rubros.set(rubrosMapeados);
          this.totalRecords.set(resp['hydra:totalItems'] || resp.totalItems || rubrosMapeados.length);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;
        },
        error: (error: any) => {
          this.loading.set(false);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;

          this.errorCount++;
          this.lastErrorTimestamp = Date.now();

          console.error(`Error al recuperar rubros (intento ${this.errorCount}/${this.MAX_ERROR_COUNT}):`, error);

          const errorDetail = this.errorCount >= this.MAX_ERROR_COUNT
            ? 'Se alcanzó el límite de reintentos. Por favor, recarga la página.'
            : `Ocurrió un error al recuperar rubros (intento ${this.errorCount}/${this.MAX_ERROR_COUNT})`;

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorDetail,
            life: this.errorCount >= this.MAX_ERROR_COUNT ? 8000 : 4000,
            sticky: this.errorCount >= this.MAX_ERROR_COUNT
          });

          this.filtersMetadata = {};
          this.multiSortMeta = [];
          this.rubros.set([]);
          this.totalRecords.set(0);
        }
      });
  }

  clear(table: Table): void {
    this.paginate = true;
    this.paginaActual = 0;
    this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE || 10;
    this.clearTableInProgress = true;
    this.filterValue = '';
    this.selectedRows = [];
    this.filtersMetadata = {};
    this.multiSortMeta = [];
    this.errorCount = 0;
    this.lastErrorTimestamp = 0;
    table.clear();
  }

  toggleRubroActivo(rubro: Rubro): void {
    if (!rubro.id) return;

    const nuevoEstado = rubro.active ?? true;

    const rubroActualizado: Rubro = {
      active: nuevoEstado
    };

    this.loading.set(true);
    this.rubroService.patch(rubro.id, rubroActualizado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: Rubro) => {
          this.loading.set(false);
          const estadoFinal = resp.active ?? false;
          const accionTxt = estadoFinal ? 'activado' : 'desactivado';
          this.messageService.add({
            severity: 'success',
            summary: `Rubro ${accionTxt}`,
            detail: `El rubro ha sido ${accionTxt} correctamente.`,
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          // Revertir el cambio en caso de error
          rubro.active = !nuevoEstado;
          console.error('Error al actualizar rubro:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el estado del rubro',
            life: 3000
          });
        }
      });
  }

  editarRubro(id: number): void {
    this.router.navigate(['/rubros/edit', id]);
  }

  navigateToCreateRubro(): void {
    this.router.navigate(['/rubros/edit/nuevo']);
  }

  eliminarRubro(rubro: Rubro): void {
    this.confirmationService.confirm({
      message: `¿Está seguro que desea desactivar el rubro "${rubro.nombre}"?`,
      header: 'Confirmar Desactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, desactivar',
      rejectLabel: 'Cancelar',
      accept: () => {
        if (!rubro.id) return;

        this.loading.set(true);
        const rubroActualizado: Rubro = {
          active: false
        };

        this.rubroService.patch(rubro.id, rubroActualizado)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.messageService.add({
                severity: 'success',
                summary: 'Rubro Desactivado',
                detail: 'El rubro ha sido desactivado correctamente',
                life: 3000
              });
              this.cargar();
            },
            error: (error) => {
              this.loading.set(false);
              console.error('Error al desactivar rubro:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo desactivar el rubro',
                life: 3000
              });
            }
          });
      }
    });
  }

  activarRubro(rubro: Rubro): void {
    if (!rubro.id) return;

    this.loading.set(true);
    const rubroActualizado: Rubro = {
      active: true
    };

    this.rubroService.patch(rubro.id, rubroActualizado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Rubro Activado',
            detail: 'El rubro ha sido activado correctamente',
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al activar rubro:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo activar el rubro',
            life: 3000
          });
        }
      });
  }
}
